// client/src/pages/admin/AdminResetPassword.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import AdminAuthLayout from '../../components/admin/layout/AdminAuthLayout';
import '../../styles/components/admin/AdminAuth.css';
import neuroLexLogo from '../../assets/images/Neurolex_Logo_New.png';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const EyeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
    <circle cx="12" cy="12" r="3"></circle>
  </svg>
);

const EyeOffIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
    <line x1="1" y1="1" x2="23" y2="23"></line>
  </svg>
);

const AdminResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [formErrors, setFormErrors] = useState({
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [tokenValid, setTokenValid] = useState(true);
  const [resetToken, setResetToken] = useState('');

  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) {
      setTokenValid(false);
      setError('Invalid or missing reset token');
    } else {
      setResetToken(token);
    }
  }, [searchParams]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: ''
      });
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const validatePassword = (password) => {
    if (!password) return "Password is required";
    if (password.length < 8) return "Password must be at least 8 characters";
    if (!/[A-Z]/.test(password)) return "Password must contain at least one uppercase letter";
    if (!/[a-z]/.test(password)) return "Password must contain at least one lowercase letter";
    if (!/[0-9]/.test(password)) return "Password must contain at least one number";
    if (!/[!@#$%^&*]/.test(password)) return "Password must contain at least one special character (!@#$%^&*)";
    return "";
  };

  const validateConfirmPassword = (confirmPassword, password) => {
    if (!confirmPassword) return "Please confirm your password";
    if (confirmPassword !== password) return "Passwords do not match";
    return "";
  };

  const validateForm = () => {
    const errors = {
      password: validatePassword(formData.password),
      confirmPassword: validateConfirmPassword(formData.confirmPassword, formData.password)
    };
    
    setFormErrors(errors);
    return !errors.password && !errors.confirmPassword;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsLoading(true);
    setError('');

    try {
      console.log('🔧 Attempting password reset');
      
      const response = await axios.post(`${API_URL}/admin/reset-password`, {
        token: resetToken,
        newPassword: formData.password
      });
      
      console.log('✅ Password reset response:', response.data);
      
      if (response.data.success) {
        setTimeout(() => {
          navigate('/admin/login', { 
            state: { message: 'Password reset successful. Please login with your new password.' }
          });
        }, 2000);
      } else {
        setError(response.data.message || 'Failed to reset password');
      }
    } catch (error) {
      console.error('❌ Reset password error:', error);
      
      if (error.response) {
        const errorMessage = error.response.data?.message || 'Failed to reset password';
        setError(errorMessage);
        
        if (errorMessage.includes('expired') || errorMessage.includes('invalid')) {
          setTokenValid(false);
        }
      } else if (error.request) {
        setError('Network error. Please check your connection.');
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const resetPasswordForm = (
    <div className="form-container">
      <div className="form-content-container">
        <img 
          src={neuroLexLogo}
          alt="Neurolex" 
          className="app-logo-small" 
        />
        
        <h1 className="form-title">RESET PASSWORD</h1>
        
        {!tokenValid ? (
          <>
            <div className="alert alert-error">
              {error || 'Invalid or expired reset link'}
            </div>
            
            <p className="form-subtitle">
              This password reset link is invalid or has expired. Please request a new one.
            </p>
            
            <Link to="/admin/forgot-password" className="admin-form-button" style={{ textAlign: 'center', display: 'block', textDecoration: 'none' }}>
              Request New Link
            </Link>
            
            <div className="account-link-container">
              <Link to="/admin/login" className="account-link">Back to Login</Link>
            </div>
          </>
        ) : (
          <>
            <p className="form-subtitle">
              Enter your new password below. Make sure it's strong and secure.
            </p>
            
            {error && (
              <div className="alert alert-error">
                {error}
              </div>
            )}
            
            <form onSubmit={handleSubmit}>
              <div className="admin-form-group">
                <label htmlFor="password" className="admin-form-label">New Password</label>
                <div className="password-input-container">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    placeholder="Enter new password"
                    value={formData.password}
                    onChange={handleChange}
                    className={`admin-form-input ${formErrors.password ? 'input-error' : ''}`}
                  />
                  <button 
                    type="button" 
                    className="password-toggle-button"
                    onClick={togglePasswordVisibility}
                  >
                    {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                  </button>
                </div>
                {formErrors.password && <div className="error-message">{formErrors.password}</div>}
                <div className="password-requirements">
                  <small>Password must contain:</small>
                  <ul>
                    <li>At least 8 characters</li>
                    <li>One uppercase and one lowercase letter</li>
                    <li>One number and one special character (!@#$%^&*)</li>
                  </ul>
                </div>
              </div>
              
              <div className="admin-form-group">
                <label htmlFor="confirmPassword" className="admin-form-label">Confirm New Password</label>
                <div className="password-input-container">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    id="confirmPassword"
                    name="confirmPassword"
                    placeholder="Confirm new password"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className={`admin-form-input ${formErrors.confirmPassword ? 'input-error' : ''}`}
                  />
                  <button 
                    type="button" 
                    className="password-toggle-button"
                    onClick={toggleConfirmPasswordVisibility}
                  >
                    {showConfirmPassword ? <EyeOffIcon /> : <EyeIcon />}
                  </button>
                </div>
                {formErrors.confirmPassword && <div className="error-message">{formErrors.confirmPassword}</div>}
              </div>
              
              <button 
                type="submit" 
                className="admin-form-button" 
                disabled={isLoading}
              >
                {isLoading ? (
                  <span className="loading-spinner"></span>
                ) : (
                  'Reset Password'
                )}
              </button>
              
              <div className="account-link-container">
                <span className="account-link-text">Remember your password?</span>
                <Link to="/admin/login" className="account-link">Back to Login</Link>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );

  return (
    <AdminAuthLayout>
      {resetPasswordForm}
    </AdminAuthLayout>
  );
};

export default AdminResetPassword;